import { useState, useRef, useEffect, useCallback } from 'react';
import type { SectorData, SectorLapResult, SectorSessionMedian, SectorSummary, SectorMetricSummary, SessionSummary } from '@/types/telemetry';
import { formatTime } from '@/lib/format';
import { createSectorTimelineChart, createSectorMaxSpeedChart, createSectorMinSpeedChart, createSectorMaxRpmChart, createSectorMinRpmChart, createSessionMedianChart } from '@/lib/charts';
import { useI18n } from '@/context/I18nContext';
import type { TranslateFn } from '@/i18n';

interface SectorPanelProps {
  sector: SectorData;
  sectorIndex: number;
  totalSectors: number;
  sessions: SessionSummary[];
  activeMetric: string;
  onMetricChange: (metric: string) => void;
}

const METRIC_TAB_KEYS = [
  { key: 'time', labelKey: 'sectorPanel.time' as const },
  { key: 'maxSpeed', labelKey: 'sectorPanel.topSpeed' as const },
  { key: 'minSpeed', labelKey: 'sectorPanel.minSpeed' as const },
  { key: 'maxRpm', labelKey: 'sectorPanel.maxRpm' as const },
  { key: 'minRpm', labelKey: 'sectorPanel.minRpm' as const },
];

function sectorHue(idx: number, total: number): number {
  return (idx * 360 / total) % 360;
}

export function SectorPanel({ sector, sectorIndex, totalSectors, sessions, activeMetric, onMetricChange }: SectorPanelProps) {
  const { t } = useI18n();
  const chartRef = useRef<HTMLDivElement>(null);
  const medianChartRef = useRef<HTMLDivElement>(null);

  const hue = sectorHue(sectorIndex, totalSectors);
  const color = `hsl(${hue}, 70%, 55%)`;
  const colorLight = `hsl(${hue}, 70%, 65%)`;
  const startPct = ((sector.startPosition || 0) * 100).toFixed(0);
  const endPct = ((sector.endPosition || 0) * 100).toFixed(0);

  const summary = sector.summary;
  const laps = sector.laps || [];

  // Compute session boundaries for chart
  const sessionBoundaries: number[] = [];
  let prevSessionId = '';
  laps.forEach((lap, i) => {
    if (lap.sessionId !== prevSessionId && i > 0) {
      sessionBoundaries.push(i);
    }
    prevSessionId = lap.sessionId;
  });

  const sessionMeta = sessions.map(s => ({ id: s.id, date: s.date, time: s.time }));

  const getMetricSummary = useCallback((): { summary: SectorSummary | SectorMetricSummary | null; label: string; unit: string; formatVal: (v: number) => string } => {
    switch (activeMetric) {
      case 'maxSpeed': return { summary: sector.maxSpeedSummary || null, label: t('sectorPanel.topSpeed'), unit: 'km/h', formatVal: (v) => v.toFixed(1) + ' km/h' };
      case 'minSpeed': return { summary: sector.minSpeedSummary || null, label: t('sectorPanel.minSpeed'), unit: 'km/h', formatVal: (v) => v.toFixed(1) + ' km/h' };
      case 'maxRpm': return { summary: sector.maxRpmSummary || null, label: t('sectorPanel.maxRpm'), unit: 'RPM', formatVal: (v) => v.toFixed(0) };
      case 'minRpm': return { summary: sector.minRpmSummary || null, label: t('sectorPanel.minRpm'), unit: 'RPM', formatVal: (v) => v.toFixed(0) };
      default: return { summary: sector.summary, label: t('sectorPanel.duration'), unit: 's', formatVal: (v) => formatTime(v) };
    }
  }, [activeMetric, sector, t]);

  // Render charts
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.innerHTML = '';

    const timer = requestAnimationFrame(() => {
      if (!chartRef.current) return;
      switch (activeMetric) {
        case 'maxSpeed':
          createSectorMaxSpeedChart(chartRef.current!, laps, sessionBoundaries);
          break;
        case 'minSpeed':
          createSectorMinSpeedChart(chartRef.current!, laps, sessionBoundaries);
          break;
        case 'maxRpm':
          createSectorMaxRpmChart(chartRef.current!, laps, sessionBoundaries);
          break;
        case 'minRpm':
          createSectorMinRpmChart(chartRef.current!, laps, sessionBoundaries);
          break;
        default:
          createSectorTimelineChart(chartRef.current!, laps, sessionBoundaries);
      }
    });

    return () => cancelAnimationFrame(timer);
  }, [activeMetric, laps, sessionBoundaries]);

  // Session median chart
  useEffect(() => {
    if (!medianChartRef.current) return;
    medianChartRef.current.innerHTML = '';

    const timer = requestAnimationFrame(() => {
      if (!medianChartRef.current) return;
      const medians = sector.sessionMedians || [];
      if (medians.length === 0) return;
      let extractor: (m: SectorSessionMedian) => number;
      let opts;
      switch (activeMetric) {
        case 'maxSpeed':
          extractor = (m) => m.medianMaxSpeedKmh || 0;
          opts = { label: t('sectorPanel.medianTopSpeed'), color: '#34d399', yLabel: 'km/h', yFormat: (v: number) => v.toFixed(0) + ' km/h', reverseGoodness: true };
          break;
        case 'minSpeed':
          extractor = (m) => m.medianMinSpeedKmh || 0;
          opts = { label: t('sectorPanel.medianMinSpeed'), color: '#60a5fa', yLabel: 'km/h', yFormat: (v: number) => v.toFixed(0) + ' km/h', reverseGoodness: true };
          break;
        case 'maxRpm':
          extractor = (m) => m.medianMaxRpm || 0;
          opts = { label: t('sectorPanel.medianMaxRpm'), color: '#fbbf24', yLabel: 'RPM', yFormat: (v: number) => v.toFixed(0), reverseGoodness: true };
          break;
        case 'minRpm':
          extractor = (m) => m.medianMinRpm || 0;
          opts = { label: t('sectorPanel.medianMinRpm'), color: '#f87171', yLabel: 'RPM', yFormat: (v: number) => v.toFixed(0), reverseGoodness: true };
          break;
        default:
          extractor = (m) => (m.medianDurationMs || 0) / 1000;
          opts = { label: t('sectorPanel.medianTime'), color: '#10b981', yLabel: 'Duration (s)', yFormat: (v: number) => v.toFixed(3) + 's', reverseGoodness: false };
      }
      createSessionMedianChart(medianChartRef.current!, medians, extractor, sessionMeta, opts);
    });

    return () => cancelAnimationFrame(timer);
  }, [activeMetric, sector.sessionMedians, sessionMeta, t]);

  const { summary: metricSummary, formatVal } = getMetricSummary();

  return (
    <div className="flex flex-col gap-4">
      {/* Summary stats row */}
      <div className="card p-4 sm:p-5" style={{ borderColor: `hsla(${hue}, 70%, 55%, 0.2)` }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
          <span className="text-sm font-semibold text-text-primary">{t('sector.sectorN', { n: sectorIndex + 1 })}</span>
          <span className="mono text-[0.68rem] text-text-muted bg-bg-surface border border-border-subtle rounded-full px-2 py-0.5 ml-2">
            {startPct}% → {endPct}%
          </span>
        </div>
        <SummaryStatsGrid summary={summary} lapsCount={laps.length} formatVal={formatTime} t={t} />
      </div>

      {/* Metric tabs */}
      <div className="p-1 rounded-xl bg-bg-surface border border-border-subtle flex gap-1 overflow-x-auto">
        {METRIC_TAB_KEYS.map(tab => (
          <button
            key={tab.key}
            onClick={() => onMetricChange(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeMetric === tab.key
                ? 'bg-accent/15 text-accent shadow-xs border border-accent/30'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-transparent'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Metric-specific summary */}
      {metricSummary && activeMetric !== 'time' && (
        <div className="card p-4">
          <MetricSummaryRow summary={metricSummary} formatVal={formatVal} t={t} />
        </div>
      )}

      {/* Timeline chart */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
            {t('sectorPanel.lapByLap')}
          </span>
        </div>
        <div ref={chartRef} className="chart-container w-full min-h-[260px]" />
      </div>

      {/* Session median chart */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
            {t('sectorPanel.sessionMedians')}
          </span>
        </div>
        <div ref={medianChartRef} className="chart-container w-full min-h-[200px]" />
      </div>
    </div>
  );
}

function SummaryStatsGrid({ summary, lapsCount, formatVal, t }: { summary: SectorSummary; lapsCount: number; formatVal: (v: number) => string; t: TranslateFn }) {
  if (!summary) return null;
  const stats = [
    { label: t('sectorPanel.best'), value: summary.bestDurationMs > 0 ? formatVal(summary.bestDurationMs) : '—' },
    { label: t('sectorPanel.median'), value: summary.medianDurationMs > 0 ? formatVal(summary.medianDurationMs) : '—' },
    { label: t('sectorPanel.avg'), value: summary.averageDurationMs > 0 ? formatVal(summary.averageDurationMs) : '—' },
    { label: t('sectorPanel.p10'), value: summary.p10DurationMs > 0 ? formatVal(summary.p10DurationMs) : '—' },
    { label: t('sectorPanel.worst'), value: summary.worstDurationMs > 0 ? formatVal(summary.worstDurationMs) : '—' },
    { label: t('sectorPanel.consistency'), value: summary.fluctuationRate != null && summary.fluctuationRate > 0 ? `${summary.fluctuationRate.toFixed(1)}%` : '—' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
      {stats.map((s, i) => (
        <div key={i} className="flex flex-col p-2.5 rounded-lg bg-bg-surface border border-border-subtle">
          <span className="text-[0.65rem] uppercase tracking-wider text-text-muted font-medium">{s.label}</span>
          <span className="text-xs font-bold text-text-primary mono mt-0.5">{s.value}</span>
        </div>
      ))}
    </div>
  );
}

function MetricSummaryRow({ summary, formatVal, t }: { summary: SectorMetricSummary; formatVal: (v: number) => string; t: TranslateFn }) {
  if (!summary) return null;
  const stats = [
    { label: t('sectorPanel.best'), value: summary.best > 0 ? formatVal(summary.best) : '—' },
    { label: t('sectorPanel.median'), value: summary.median > 0 ? formatVal(summary.median) : '—' },
    { label: t('sectorPanel.worst'), value: summary.worst > 0 ? formatVal(summary.worst) : '—' },
    { label: t('sectorPanel.avg'), value: summary.average > 0 ? formatVal(summary.average) : '—' },
    { label: t('sectorPanel.p10'), value: summary.p10 > 0 ? formatVal(summary.p10) : '—' },
    { label: t('sectorPanel.consistency'), value: summary.fluctuationRate != null && summary.fluctuationRate > 0 ? `${summary.fluctuationRate.toFixed(1)}%` : '—' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
      {stats.map((s, i) => (
        <div key={i} className="flex flex-col p-2.5 rounded-lg bg-bg-surface border border-border-subtle">
          <span className="text-[0.65rem] uppercase tracking-wider text-text-muted font-medium">{s.label}</span>
          <span className="text-xs font-bold text-text-primary mono mt-0.5">{s.value}</span>
        </div>
      ))}
    </div>
  );
}

function SectorLapsTable({ laps, formatTime, t }: { laps: SectorLapData[]; formatTime: (ms: number) => string; t: TranslateFn }) {
  if (laps.length === 0) return <p className="text-sm text-text-muted">{t('common.noData')}</p>;

  const sorted = [...laps].sort((a, b) => a.sectorDurationMs - b.sectorDurationMs);

  return (
    <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
      <table className="w-full text-xs border-collapse">
        <thead className="divider sticky top-0 bg-bg-surface z-10">
          <tr>
            <th className="text-left py-2 px-3 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">#</th>
            <th className="text-left py-2 px-3 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('sectorPanel.session')}</th>
            <th className="text-left py-2 px-3 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('sectorPanel.lap')}</th>
            <th className="text-left py-2 px-3 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('sectorPanel.time')}</th>
            <th className="text-left py-2 px-3 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('sectorPanel.maxSpeed')}</th>
            <th className="text-left py-2 px-3 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('sectorPanel.maxRpm')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((lap, i) => (
            <tr key={`${lap.sessionId}-${lap.lapNumber}`} className={`hover:bg-bg-hover transition-colors ${i < sorted.length - 1 ? 'divider' : ''}`}>
              <td className="py-2 px-3 text-text-muted mono">{i + 1}</td>
              <td className="py-2 px-3 text-text-secondary mono truncate max-w-[100px]">
                {(lap.sessionTime || lap.sessionId || '').slice(-8)}
              </td>
              <td className="py-2 px-3 text-text-secondary mono">L{lap.lapNumber}</td>
              <td className={`py-2 px-3 mono font-semibold ${i === 0 ? 'text-green' : ''}`}>
                {formatTime(lap.sectorDurationMs)}
              </td>
              <td className="py-2 px-3 mono text-text-secondary">
                {lap.maxSpeedRaw ? ((lap.maxSpeedRaw || 0) / 10).toFixed(1) : '—'}
              </td>
              <td className="py-2 px-3 mono text-text-secondary">
                {lap.maxRpm || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
